import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format } from 'date-fns'

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const PROFILE_TIMES = ['0000', '1200'];

// Sort integration-time tokens the same way the server does: 'default' (no
// MIN suffix) first, then ascending numeric MIN value.
const sortIntegrationTimes = (entries) => {
  return [...entries].sort((a, b) => {
    if (a.value === 'default' && b.value === 'default') return 0;
    if (a.value === 'default') return -1;
    if (b.value === 'default') return 1;
    return parseInt(a.value, 10) - parseInt(b.value, 10);
  });
};

// Merge the integration times available at 00/12 UTC into a single sorted, deduplicated list
const mergeIntegrationTimes = (lists) => {
  const byValue = new Map();
  lists.flat().forEach(t => byValue.set(t.value, t));
  return sortIntegrationTimes(Array.from(byValue.values()));
};

function ProfilesViewer({ selectedDateProfile, selectedCampaign }) {
  const [selectedTemperatureProfile, setSelectedTemperatureProfile] = useState(null);  // Temperature profile plot for the selected date
  const [selectedTemperatureProfile00, setSelectedTemperatureProfile00] = useState(null);  // Temperature profile plot for the selected date
  const [selectedTemperatureProfile12, setSelectedTemperatureProfile12] = useState(null);  // Temperature profile plot for the selected date
  const [selectedWindProfile00, setSelectedWindProfile00] = useState(null);  // Wind profile plot for the selected date
  const [selectedWindProfile12, setSelectedWindProfile12] = useState(null);  // Wind profile plot for the selected date
  const [windType, setWindType] = useState('speed-dir'); // 'speed-dir' or 'uv' or 'uv_difference'
  const [temperatureType, setTemperatureType] = useState('normal'); // 'normal' or 'difference'
  const [temperatureIntegrationTime, setTemperatureIntegrationTime] = useState(null); // Shared across 00/12 UTC
  const [windIntegrationTime, setWindIntegrationTime] = useState(null); // Shared across 00/12 UTC
  const [availableTemperatureIntegrationTimes, setAvailableTemperatureIntegrationTimes] = useState([]);
  const [availableWindIntegrationTimes, setAvailableWindIntegrationTimes] = useState([]);
  const setters_wind = [setSelectedWindProfile00, setSelectedWindProfile12]
  const setters_temperature = [setSelectedTemperatureProfile00, setSelectedTemperatureProfile12]

  // Fetch the available integration times (union of what's available at 00 and 12 UTC)
  useEffect(() => {
    if (!selectedDateProfile || !selectedCampaign) {
      setAvailableTemperatureIntegrationTimes([]);
      setAvailableWindIntegrationTimes([]);
      return;
    }

    const formattedDateProfile = format(selectedDateProfile, 'yyyy-MM-dd');
    const temperatureSuffix = `${temperatureType === 'difference' ? 'refoperan_' : ''}`;

    Promise.all(PROFILE_TIMES.map(time =>
      fetch(`${API_BASE}/api/temperature-times/${selectedCampaign}/${temperatureSuffix}${formattedDateProfile}_${time.slice(0, 2)}`, {
        credentials: 'include'
      }).then(res => res.ok ? res.json() : { times: [] }).catch(() => ({ times: [] }))
    )).then(results => {
      const merged = mergeIntegrationTimes(results.map(r => r.times));
      setAvailableTemperatureIntegrationTimes(merged);
      setTemperatureIntegrationTime(prev => (merged.some(t => t.value === prev) ? prev : (merged[0]?.value ?? null)));
    });

    Promise.all(PROFILE_TIMES.map(time => {
      const windSuffix = `_${windType === 'uv' ? `uv_${time}` : windType === 'uv_difference' ? `uv_refoperan_${time}` : time}`;
      return fetch(`${API_BASE}/api/wind-times/${selectedCampaign}/${formattedDateProfile}${windSuffix}`, {
        credentials: 'include'
      }).then(res => res.ok ? res.json() : { times: [] }).catch(() => ({ times: [] }));
    })).then(results => {
      const merged = mergeIntegrationTimes(results.map(r => r.times));
      setAvailableWindIntegrationTimes(merged);
      setWindIntegrationTime(prev => (merged.some(t => t.value === prev) ? prev : (merged[0]?.value ?? null)));
    });
  }, [selectedDateProfile, selectedCampaign, windType, temperatureType]);

  // Fetch the profile plot for selected date, campaign, and integration time
  useEffect(() => {
    if (!selectedDateProfile || !selectedCampaign) {
      // Clear all plots if no date or campaign selected
      setSelectedTemperatureProfile(null);
      setSelectedTemperatureProfile00(null);
      setSelectedTemperatureProfile12(null);
      setSelectedWindProfile00(null);
      setSelectedWindProfile12(null);
      return;
    }

    const formattedDateProfile = format(selectedDateProfile, 'yyyy-MM-dd') // selectedDate.toISOString().split('T')[0];

    fetch(`${API_BASE}/api/temperature/${selectedCampaign}/${formattedDateProfile}`, {
      credentials: 'include' // Include session cookie
    })
      .then(res => res.ok ? res.json() : Promise.reject('Plot not found'))
      .then(data => {
        console.log('Plot data:', data);
        setSelectedTemperatureProfile(`${API_BASE}${data.original}`);
      })
      .catch(err => {
        console.error('Error fetching selected plot:', err);
        setSelectedTemperatureProfile(null);
      });

    PROFILE_TIMES.forEach((time, index) => {
      const windSuffix = `_${windType === 'uv' ? `uv_${time}` : windType === 'uv_difference' ? `uv_refoperan_${time}`: time}`;
      const windParams = new URLSearchParams();
      if (windIntegrationTime) windParams.set('integration', windIntegrationTime);

      fetch(`${API_BASE}/api/wind/${selectedCampaign}/${formattedDateProfile}${windSuffix}?${windParams.toString()}`, {
        credentials: 'include' // Include session cookie
      })
        .then(res => res.ok ? res.json() : Promise.reject('Plot not found'))
        .then(data => setters_wind[index](`${API_BASE}${data.original}`))
        .catch(err => {
          console.error('Error fetching selected plot:', err);
          setters_wind[index](null);
        });

      const temperatureSuffix = `${temperatureType === 'difference' ? 'refoperan_' : ''}`;
      const temperatureParams = new URLSearchParams();
      if (temperatureIntegrationTime) temperatureParams.set('integration', temperatureIntegrationTime);

      fetch(`${API_BASE}/api/temperature/${selectedCampaign}/${temperatureSuffix}${formattedDateProfile}_${time.slice(0,2)}?${temperatureParams.toString()}`, {
        credentials: 'include' // Include session cookie
      })
        .then(res => res.ok ? res.json() : Promise.reject('Plot not found'))
        .then(data => setters_temperature[index](`${API_BASE}${data.original}`))
        .catch(err => {
          console.error('Error fetching selected plot:', err);
          setters_temperature[index](null);
      });
    });


  }, [selectedDateProfile, selectedCampaign, windType, temperatureType, windIntegrationTime, temperatureIntegrationTime]);

  return (
    <div className="content-card">
      <h2>Profiles and Comparisons</h2>
      
      {(selectedCampaign === 'Test' || selectedCampaign === 'Kuehlungsborn' || selectedCampaign === 'Test_Andoya') && (
        <div className="warning-banner">
          ⚠️ Warning - currently with dummy lidar data!
        </div>
      )}      
      
      {/* Temperature Profiles Section */}
      <div style={{ marginBottom: 'var(--space-xl)' }}>
        <div className="plot-controls">
          <h4 style={{ margin: 0 }}>Temperature profiles for {selectedCampaign} — {format(selectedDateProfile, 'yyyy-MM-dd')}</h4>
          <div className="button-group">
            <button
              onClick={() => setTemperatureType('normal')}
              className={`button ${temperatureType === 'normal' ? 'active' : ''}`}
            >
              Normal
            </button>
            <button
              onClick={() => setTemperatureType('difference')}
              className={`button ${temperatureType === 'difference' ? 'active' : ''}`}
            >
              O - B
            </button>
          </div>
          {availableTemperatureIntegrationTimes.length > 1 && (
            <select
              className="modern-select"
              value={temperatureIntegrationTime || ''}
              onChange={(e) => setTemperatureIntegrationTime(e.target.value)}
            >
              {availableTemperatureIntegrationTimes.map(time => (
                <option key={time.value} value={time.value}>
                  {time.label}
                </option>
              ))}
            </select>
          )}
        </div>

        {(selectedTemperatureProfile00 || selectedTemperatureProfile12) ? (
          <div className="profiles-container-temperature">
            {selectedTemperatureProfile00 && (
              <img src={selectedTemperatureProfile00} alt="Temperature profile 00UTC" />
            )}
            {selectedTemperatureProfile12 && (
              <img src={selectedTemperatureProfile12} alt="Temperature profile 12UTC" />
            )}
          </div>
        ) : (selectedTemperatureProfile && temperatureType === 'normal') ? (
          <div className="profiles-container-temperature">
            <img src={selectedTemperatureProfile} alt="Temperature profile" />
          </div>
        ) : selectedDateProfile && selectedCampaign ? (
          <div className="empty-state">
            <p>No temperature profile plot {temperatureType === 'difference' ? '(O - B)' : ''} available for this date and campaign.</p>
          </div>
        ) : !selectedCampaign ? (
          <div className="empty-state">
            <p>Please select a campaign site.</p>
          </div>
        ) : null}
      </div>

      {/* Wind Profiles Section */}
      <div>
        <div className="plot-controls">
          <h4 style={{ margin: 0 }}>Wind profiles for {selectedCampaign} — {format(selectedDateProfile, 'yyyy-MM-dd')}</h4>
          <div className="button-group">
            <button
              onClick={() => setWindType('speed-dir')}
              className={`button ${windType === 'speed-dir' ? 'active' : ''}`}
            >
              Speed & Direction
            </button>
            <button
              onClick={() => setWindType('uv')}
              className={`button ${windType === 'uv' ? 'active' : ''}`}
            >
              Zonal & Meridional 
            </button>
            <button
              onClick={() => setWindType('uv_difference')}
              className={`button ${windType === 'uv_difference' ? 'active' : ''}`}
            >
              O - B
            </button>
          </div>
          {availableWindIntegrationTimes.length > 1 && (
            <select
              className="modern-select"
              value={windIntegrationTime || ''}
              onChange={(e) => setWindIntegrationTime(e.target.value)}
            >
              {availableWindIntegrationTimes.map(time => (
                <option key={time.value} value={time.value}>
                  {time.label}
                </option>
              ))}
            </select>
          )}
        </div>

        {(selectedWindProfile00 || selectedWindProfile12) ? (
          <div className="profiles-container-wind">
            {selectedWindProfile00 && (
              <img src={selectedWindProfile00} alt="Profile 00" />
            )}
            {selectedWindProfile12 && (
              <img src={selectedWindProfile12} alt="Profile 12" />
            )}
          </div>
        ) : selectedDateProfile && selectedCampaign ? (
          <div className="empty-state">
            <p>No wind profile plot ({windType === 'uv_difference' ? 'O - B' : windType === 'uv' ? 'zonal / meridional': 'speed / direction'}) available for this date and campaign.</p>
          </div>
        ) : !selectedCampaign ? (
          <div className="empty-state">
            <p>Please select a campaign site.</p>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default ProfilesViewer;
